import React, { Fragment, useEffect, useMemo } from 'react';

import Alert from '@amzn/meridian/alert';
import Column from '@amzn/meridian/column';
import Divider from '@amzn/meridian/divider';

import {
    CellValueChangedEvent,
    ColumnState,
    ICellEditorParams,
    RowClassParams,
    RowClassRules
} from 'ag-grid-community';
import { cloneDeep, isEmpty, sortBy } from 'lodash';
import { BaseAgGrid } from 'src/components/BaseAgGrid';
import { EXCEL_DATE_STYLE_ID } from 'src/constants/agGrid';
import { BREADCRUMB_HEIGHT, FOOTER_HEIGHT, HEIGHT_ADJUSTMENT, TOPBAR_HEIGHT } from 'src/constants/style';
import { AddDealContext } from 'src/contexts/addDealContext';
import { usePrevious } from 'src/hooks/usePrevious';
import { IRateCardValues, ISpecifyDealAgGridRowData, ISpecifyDealAgGridRowErrorState } from 'src/types/IAddDeal';
import { getWindowSize } from 'src/util/PlanMappings';
import { emptyCellFormatter } from 'src/util/grid/cellFormatter';
import { dateFilterOptions } from 'src/util/grid/filter';
import { dateComparator, dateFilterComparator } from 'src/util/grid/sort';

import { DateCellEditor } from './DateCellEditor';
import { RateCardModule } from './RateCardModule';
import './SpecifyDeal.scss';
import { VendorCellRenderer } from './VendorCellRenderer';

export const SpecifyDeal = () => {
    const {
        services,
        rateCardValues,
        setRateCardValues,
        rateCardErrorStates,
        setRateCardErrorStates,
        selectedVendors,
        specifyDealGridRowData,
        setSpecifyDealGridRowData,
        specifyDealGridRowErrorStates,
        setSpecifyDealGridRowErrorStates,
        serviceToRateCardNameListObj,
        rateCardNameToIdObj
    } = React.useContext(AddDealContext);
    const prevRateCardValues = usePrevious(rateCardValues);
    const [specifyDealGridErrorRowCount, setSpecifyDealGridErrorRowCount] = React.useState(0);

    const rateCardCellSelector = (params: ICellEditorParams<ISpecifyDealAgGridRowData>) => {
        return {
            component: 'agRichSelectCellEditor',
            params: {
                values: serviceToRateCardNameListObj[params.data.service!].map(
                    (optionStr) => JSON.parse(optionStr)['contractName']
                )
            },
            popup: true
        };
    };

    const colDefs = [
        {
            headerName: 'Vendor',
            field: 'entityName',
            cellRenderer: VendorCellRenderer,
            suppressSizeToFit: true,
            flex: 2
        },
        {
            headerName: 'Service',
            field: 'service',
            suppressSizeToFit: true,
            flex: 1
        },
        {
            headerName: 'Rate card',
            field: 'contractName',
            editable: true,
            cellEditorSelector: rateCardCellSelector,
            valueFormatter: emptyCellFormatter,
            suppressSizeToFit: true,
            flex: 2
        },
        {
            headerName: 'Start date',
            field: 'startDate',
            comparator: dateComparator,
            filter: 'agDateColumnFilter',
            filterParams: {
                filterOptions: dateFilterOptions,
                defaultOption: 'greaterThanOrEqual',
                comparator: dateFilterComparator,
                includeBlanksInLessThan: true,
                includeBlanksInGreaterThan: true
            },
            valueFormatter: emptyCellFormatter,
            editable: true,
            cellClass: EXCEL_DATE_STYLE_ID,
            cellEditor: DateCellEditor,
            cellEditorParams: {
                isStartDate: true
            },
            suppressSizeToFit: true,
            flex: 1
        },
        {
            headerName: 'End date',
            field: 'endDate',
            comparator: dateComparator,
            filter: 'agDateColumnFilter',
            filterParams: {
                filterOptions: dateFilterOptions,
                defaultOption: 'lessThanOrEqual',
                comparator: dateFilterComparator,
                includeBlanksInLessThan: true,
                includeBlanksInGreaterThan: true
            },
            valueFormatter: emptyCellFormatter,
            editable: true,
            cellClass: EXCEL_DATE_STYLE_ID,
            cellEditor: DateCellEditor,
            cellEditorParams: {
                isStartDate: false
            },
            suppressSizeToFit: true,
            flex: 1
        },
        {
            headerName: 'Comments',
            field: 'comment',
            filter: false,
            editable: true,
            suppressSizeToFit: true,
            flex: 2
        }
    ];
    const sortModel: ColumnState[] = [
        { colId: 'entityName', sort: 'asc', sortIndex: 0 },
        { colId: 'service', sort: 'asc', sortIndex: 1 }
    ];

    const rowClassRules = useMemo<RowClassRules>(() => {
        return {
            'red-error-row': (params: RowClassParams) => {
                return specifyDealGridRowErrorStates[params.node.rowIndex!].hasError;
            }
        };
    }, [specifyDealGridRowErrorStates]);

    const genInitialRowData = () => {
        return selectedVendors.reduce((acc: ISpecifyDealAgGridRowData[], curr) => {
            for (const service in rateCardValues) {
                const row = { ...rateCardValues[service] } as ISpecifyDealAgGridRowData;
                row['entityName'] = curr.name;
                row['entityId'] = curr.entityId;
                acc.push(row);
            }
            return acc;
        }, []);
    };

    const genInitialErrorStatesData = () => {
        return selectedVendors.reduce((acc: ISpecifyDealAgGridRowErrorState[], curr) => {
            for (const service in rateCardValues) {
                const row = { hasError: false, errorMsg: '' } as ISpecifyDealAgGridRowErrorState;
                acc.push(row);
            }
            return acc;
        }, []);
    };

    const getUpdatedServiceName = (prevRateCardValues: IRateCardValues, currRateCardValues: IRateCardValues) => {
        for (const service of services) {
            if (
                JSON.stringify(prevRateCardValues[service.serviceName!]) !==
                JSON.stringify(currRateCardValues[service.serviceName!])
            ) {
                return service.serviceName;
            }
        }
        return null;
    };

    useEffect(() => {
        if (!prevRateCardValues) {
            setSpecifyDealGridRowData(!isEmpty(specifyDealGridRowData) ? specifyDealGridRowData : genInitialRowData());
            setSpecifyDealGridRowErrorStates(genInitialErrorStatesData());
        } else {
            const updatedServiceName = getUpdatedServiceName(prevRateCardValues, rateCardValues);
            const clonedRowData: ISpecifyDealAgGridRowData[] = cloneDeep(specifyDealGridRowData);
            const newRowData = clonedRowData.map((row) => {
                return row.service === updatedServiceName ? { ...row, ...rateCardValues[updatedServiceName!] } : row;
            });
            setSpecifyDealGridRowData(newRowData);
        }
    }, [rateCardValues]);

    useEffect(() => {
        if (!isEmpty(specifyDealGridRowErrorStates)) {
            const errorCount = specifyDealGridRowErrorStates.reduce((acc, curr) => {
                if (curr.hasError) {
                    acc++;
                }
                return acc;
            }, 0);
            setSpecifyDealGridErrorRowCount(errorCount);
        }
    }, [specifyDealGridRowErrorStates]);

    const cellValueChangeHandler = (event: CellValueChangedEvent<any, any>) => {
        const { data, column, newValue } = event;
        const newRowData = specifyDealGridRowData.map((row) => {
            if (row.service === data.service && row.entityName === data.entityName) {
                if (column.getColId() === 'contractName') {
                    return { ...row, contractName: newValue, contractId: rateCardNameToIdObj[newValue] };
                } else {
                    return { ...row, [column.getColId()]: newValue };
                }
            }
            return row;
        });
        setSpecifyDealGridRowData(newRowData);
    };

    return (
        <div className="specify-deal-div">
            <div
                className="left-control"
                style={{
                    height:
                        getWindowSize().height -
                        (TOPBAR_HEIGHT + BREADCRUMB_HEIGHT + FOOTER_HEIGHT + HEIGHT_ADJUSTMENT / 2)
                }}
            >
                {sortBy(services, ['serviceId']).map((service, index) => (
                    <Fragment key={index}>
                        <RateCardModule
                            rateCardInfo={rateCardValues[service.serviceName!]}
                            rateCardErrorState={rateCardErrorStates[service.serviceName!]}
                            setRateCardValues={setRateCardValues}
                            setRateCardErrorStates={setRateCardErrorStates}
                        />
                        {index !== services.length - 1 ? <Divider /> : null}
                    </Fragment>
                ))}
            </div>
            <Column className="right-grid">
                {specifyDealGridErrorRowCount > 0 && (
                    <Alert size="medium" type="error">
                        There {specifyDealGridErrorRowCount === 1 ? 'is' : 'are'} {specifyDealGridErrorRowCount} error
                        {specifyDealGridErrorRowCount === 1 ? '' : 's'}.
                    </Alert>
                )}
                <BaseAgGrid
                    columnDefs={colDefs}
                    rowData={specifyDealGridRowData}
                    sortModel={sortModel}
                    onCellValueChanged={cellValueChangeHandler}
                    rowClassRules={rowClassRules}
                />
            </Column>
        </div>
    );
};
